import { ApolloError } from '@apollo/client'
import { isBefore, subDays, format, setMinutes, differenceInMinutes } from 'date-fns'
import { findLast, isEmpty } from 'lodash'
import { IntlShape, MessageDescriptor, defineMessages } from 'react-intl'

import {
  EPunctualityStatus,
  ETripTimelineEventType,
  IChangeDriverForm,
  INoteFormValues,
  ITimelineFilterValues,
  ITripStopStatus
} from 'features/Trips/types'
import {
  AddTripDriverRsvpNoteInput,
  RegisterTripOdometerDistanceInput,
  TripDriverRsvpNoteStatus,
  TripV2ItineraryStopCheck
} from 'gql/generated/graphql'
import {
  GTAddTripNoteInput,
  GTCashFlowInput,
  GTCashFlowTypeInput,
  GTCancellationCategory,
  GTCancellationSubcategory,
  GTPaymentIncidencePaymentMethodInput,
  GTTripNoteCategoryInput,
  GTCreateRouteServiceVehicleScheduleInput,
  GTRouteServiceScheduleFrequencyUnit,
  GTChangeType,
  GTTripV2,
  GTTripV2VehicleScheduleType,
  GTTripV2ConfirmationModalitySalesChannel,
  GTTripV2ConfirmationModalityName,
  GTTripV2Vertical
} from 'gql/types.generated'
import { mapDayToOperationDay } from 'models/services'
import { getDayLabel } from 'resolvers/services'
import { US_DATE_FORMAT, dateToString, parseDate } from 'utils/date'
import { integerToCents, kilometersToMeters } from 'utils/number'
import {
  TripV2,
  TripV2CancellationReasonCategory,
  TripV2CancellationReasonSubcategory,
  TripV2ItineraryStop,
  TripV2ItineraryStopCheckMethod,
  TripV2TimelineEvent,
  TripV2TimelineEventTripCancellationReason
} from 'gql/generated/graphql'
import { safeFormatMessage } from 'utils/intl'
import { convert24HourToMinutes, convertHour24ToHour12Time, dateToTimeString } from 'utils/time'
import { punctualityThresholds } from 'features/Trips/constants'

export const checkInMessages = defineMessages({
  [TripV2ItineraryStopCheckMethod.Automatic]: {
    id: 'trip.checkIn.method.automatic',
    defaultMessage: 'Automatic'
  },
  [TripV2ItineraryStopCheckMethod.Manual]: {
    id: 'trip.checkIn.method.manual',
    defaultMessage: 'Manual'
  }
})

export const punctualityStatusMessages = defineMessages({
  [EPunctualityStatus.EARLY]: {
    id: 'trip.punctuality.early',
    defaultMessage: 'Early'
  },
  [EPunctualityStatus.ON_TIME]: {
    id: 'trip.punctuality.onTime',
    defaultMessage: 'On time'
  },
  [EPunctualityStatus.MODERATE]: {
    id: 'trip.punctuality.moderate',
    defaultMessage: 'Moderate'
  },
  [EPunctualityStatus.SEVERE]: {
    id: 'trip.punctuality.severe',
    defaultMessage: 'Severe'
  }
})

export const eventTypeMessages = defineMessages({
  [ETripTimelineEventType.TripCancelled]: {
    id: 'trip.timeline.event.type.tripCancelled',
    defaultMessage: 'Trip cancelled'
  },
  [ETripTimelineEventType.TripVehicleChanged]: {
    id: 'trip.timeline.event.type.tripVehicleChanged',
    defaultMessage: 'Trip vehicle changed'
  },
  [ETripTimelineEventType.TripItineraryChanged]: {
    id: 'trip.timeline.event.type.tripItineraryChanged',
    defaultMessage: 'Trip itinerary changed'
  },
  [ETripTimelineEventType.TripRouteChanged]: {
    id: 'trip.timeline.event.type.tripRouteChanged',
    defaultMessage: 'Trip route changed'
  },
  [ETripTimelineEventType.TripScheduled]: {
    id: 'trip.timeline.event.type.tripScheduled',
    defaultMessage: 'Trip created'
  },
  [ETripTimelineEventType.TripSaleDeactivated]: {
    id: 'trip.timeline.event.type.tripSaleDeactivated',
    defaultMessage: 'Trip sale deactivated'
  },
  [ETripTimelineEventType.TripSaleActivated]: {
    id: 'trip.timeline.event.type.tripSaleActivated',
    defaultMessage: 'Trip sale activated'
  },
  [ETripTimelineEventType.TripDriverCheckedIn]: {
    id: 'trip.timeline.event.type.tripDriverCheckedIn',
    defaultMessage: 'Driver starts trip'
  },
  [ETripTimelineEventType.TripDriverStopCheckedIn]: {
    id: 'trip.timeline.event.type.tripDriverStopCheckedIn',
    defaultMessage: 'Driver stop checked in'
  },
  [ETripTimelineEventType.TripDriverStopCheckedOut]: {
    id: 'trip.timeline.event.type.tripDriverCheckedOut',
    defaultMessage: 'Driver stop checked out'
  },
  [ETripTimelineEventType.TripCompleted]: {
    id: 'trip.timeline.event.type.tripCompleted',
    defaultMessage: 'Driver ends trip'
  },
  [ETripTimelineEventType.TripDriverRSVPDeclined]: {
    id: 'trip.timeline.event.type.tripDriverRSVPDeclined',
    defaultMessage: 'Driver declined trip'
  },
  [ETripTimelineEventType.TripDriverRSVPConfirmed]: {
    id: 'trip.timeline.event.type.tripDriverRSVPConfirmed',
    defaultMessage: 'Driver confirmed trip'
  },
  [ETripTimelineEventType.TripDriverRSVPMissed]: {
    id: 'trip.timeline.event.type.tripDriverRSVPMissed',
    defaultMessage: "Driver hasn't confirmed"
  },
  [ETripTimelineEventType.TripStopCheckedIn]: {
    id: 'trip.timeline.event.type.tripStopCheckedIn',
    defaultMessage: 'Trip stop checked in'
  },
  [ETripTimelineEventType.TripStopCheckedOut]: {
    id: 'trip.timeline.event.type.tripStopCheckedOut',
    defaultMessage: 'Trip stop checked out'
  }
})

export const cancellationCategoryMessages = defineMessages({
  [TripV2CancellationReasonCategory.Vehicle]: {
    id: 'trip.cancellation.category.vehicle',
    defaultMessage: 'Vehicle'
  },
  [TripV2CancellationReasonCategory.ExternalConditions]: {
    id: 'trip.cancellation.category.externalConditions',
    defaultMessage: 'External conditions'
  },
  [TripV2CancellationReasonCategory.Planning]: {
    id: 'trip.cancellation.category.planning',
    defaultMessage: 'Planning'
  },
  [TripV2CancellationReasonCategory.Partner]: {
    id: 'trip.cancellation.category.partner',
    defaultMessage: 'Partner'
  },
  [TripV2CancellationReasonCategory.Driver]: {
    id: 'trip.cancellation.category.driver',
    defaultMessage: 'Driver'
  },
  [TripV2CancellationReasonCategory.BusinessClient]: {
    id: 'trip.cancellation.category.businessClient',
    defaultMessage: 'Business client'
  },
  [TripV2CancellationReasonCategory.Other]: {
    id: 'trip.cancellation.category.other',
    defaultMessage: 'Other'
  }
})

export const cancellationSubcategoryMessages = defineMessages({
  [TripV2CancellationReasonSubcategory.Absence]: {
    id: 'trips.cancellation.subcategory.absence',
    defaultMessage: 'Absence'
  },
  [TripV2CancellationReasonSubcategory.Accident]: {
    id: 'trips.cancellation.subcategory.accident',
    defaultMessage: 'Accident'
  },
  [TripV2CancellationReasonSubcategory.Disability]: {
    id: 'trips.cancellation.subcategory.disability',
    defaultMessage: 'Disability'
  },
  [TripV2CancellationReasonSubcategory.MechanicalFailure]: {
    id: 'trips.cancellation.subcategory.mechanicalFailure',
    defaultMessage: 'Mechanical failure'
  },
  [TripV2CancellationReasonSubcategory.Other]: {
    id: 'trips.cancellation.subcategory.other',
    defaultMessage: 'Other'
  },
  [TripV2CancellationReasonSubcategory.Protest]: {
    id: 'trips.cancellation.subcategory.protest',
    defaultMessage: 'Protest'
  },
  [TripV2CancellationReasonSubcategory.Roadblock]: {
    id: 'trips.cancellation.subcategory.roadblock',
    defaultMessage: 'Roadblock'
  },
  [TripV2CancellationReasonSubcategory.ScheduledMaintenance]: {
    id: 'trips.cancellation.subcategory.scheduledMaintenance',
    defaultMessage: 'Scheduled maintenance'
  },
  [TripV2CancellationReasonSubcategory.Security]: {
    id: 'trips.cancellation.subcategory.security',
    defaultMessage: 'Security'
  },
  [TripV2CancellationReasonSubcategory.Traffic]: {
    id: 'trips.cancellation.subcategory.traffic',
    defaultMessage: 'Traffic'
  },
  [TripV2CancellationReasonSubcategory.UnitStoppage]: {
    id: 'trips.cancellation.subcategory.unitStoppage',
    defaultMessage: 'Unit stoppage'
  },
  [TripV2CancellationReasonSubcategory.Weather]: {
    id: 'trips.cancellation.subcategory.weather',
    defaultMessage: 'Weather'
  }
})

export const getTimelineDotColor = (type: string): string => {
  switch (type) {
    case ETripTimelineEventType.TripScheduled:
      return '#00C875'
    case ETripTimelineEventType.TripCancelled:
      return '#FF627B'
    case ETripTimelineEventType.TripItineraryChanged:
      return '#FFA800'
    case ETripTimelineEventType.TripRouteChanged:
      return '#E27ADD'
    case ETripTimelineEventType.TripVehicleChanged:
      return '#8F8BFF'
    case ETripTimelineEventType.TripSaleActivated:
      return '#27EDC7'
    case ETripTimelineEventType.TripSaleDeactivated:
      return '#B0B0B0'
    case ETripTimelineEventType.TripDriverCheckedIn:
      return '#00C875'
    case ETripTimelineEventType.TripDriverStopCheckedIn:
      return '#5D59D7'
    case ETripTimelineEventType.TripDriverStopCheckedOut:
      return '#8F8BFF'
    case ETripTimelineEventType.TripCompleted:
      return '#423F9A'
    case ETripTimelineEventType.TripDriverRSVPDeclined:
      return '#FF627B'
    case ETripTimelineEventType.TripDriverRSVPConfirmed:
      return '#27EDC7'
    case ETripTimelineEventType.TripDriverRSVPMissed:
      return '#FFCED6'
    case ETripTimelineEventType.TripStopCheckedIn:
      return '#A86EEB'
    case ETripTimelineEventType.TripStopCheckedOut:
      return '#CFB1F3'
    default:
      return '#5d59d7'
  }
}

export const isLinkableEvent = (event: string): boolean => {
  return Object.values(GTChangeType).includes(event as GTChangeType)
}

export const getCancellationReasonLabel = (
  intl: IntlShape,
  tripReason?: TripV2TimelineEventTripCancellationReason | null
): string | undefined => {
  if (isEmpty(tripReason)) return undefined

  let reason = safeFormatMessage({ intl, messages: cancellationCategoryMessages, key: tripReason.category ?? '' })
  if (reason && tripReason.subcategory) {
    const subcategory = safeFormatMessage({
      intl,
      messages: cancellationSubcategoryMessages,
      key: tripReason.subcategory
    })
    reason += ` (${subcategory})`
  }
  return reason
}

export const filterTimelineEvents = (
  timeline: TripV2TimelineEvent[],
  filters: ITimelineFilterValues
): TripV2TimelineEvent[] => {
  let filteredTimeline = [...timeline]
  if (!isEmpty(filters.time)) {
    let now = new Date()
    now = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    const threshold = subDays(now, parseInt(filters.time))
    filteredTimeline = filteredTimeline.filter((event) => isBefore(threshold, new Date(event.createdAt)))
  }
  if (!isEmpty(filters.user) && !isEmpty(filters.user.username)) {
    filteredTimeline = filteredTimeline.filter((event) => event.user.username === filters.user.username)
  }
  if (!isEmpty(filters.event)) {
    filteredTimeline = filteredTimeline.filter((event) => event.type === filters.event)
  }

  return filteredTimeline
}

export enum CREATE_CAMPAIGN_SUBCAMPAIGN_ERRORS {
  NO_BOOKINGS_AFFECTED = 'no_bookings_affected',
  TRIP_SEGMENT_CONFLICT = 'trip_segment_conflict'
}

const messages = defineMessages({
  noBookingsAffected: {
    id: 'error.noBookingsAffected',
    defaultMessage: "Can’t create incidence because segment doesn't have bookings"
  },
  tripSegmentConflict: {
    id: 'error.tripSegmentConflict',
    defaultMessage: 'The segment is already in a trip'
  },
  defaultError: {
    id: 'error.defaultError',
    defaultMessage: 'There was an error creating incidence coupons'
  }
})

const createCampaignSubcampaignErrors: Record<string, MessageDescriptor> = {
  [CREATE_CAMPAIGN_SUBCAMPAIGN_ERRORS.NO_BOOKINGS_AFFECTED]: messages.noBookingsAffected,
  [CREATE_CAMPAIGN_SUBCAMPAIGN_ERRORS.TRIP_SEGMENT_CONFLICT]: messages.tripSegmentConflict
}

export function getCampaignSubcampaignErrorMessage(error: ApolloError): MessageDescriptor {
  const FIRST_ERROR_POSITION = 0
  const errorCode = error?.graphQLErrors?.[FIRST_ERROR_POSITION]?.extensions?.code
  const errorMessage = createCampaignSubcampaignErrors[errorCode]
  return errorMessage ? errorMessage : messages.defaultError
}

export const mapNoteFormToInput = (tripId: string, note: INoteFormValues): GTAddTripNoteInput => {
  const payments = [
    {
      method: GTPaymentIncidencePaymentMethodInput.Cash,
      amount: integerToCents(note.cashAmount)
    },
    {
      method: GTPaymentIncidencePaymentMethodInput.Card,
      amount: integerToCents(note.cardAmount)
    }
  ].filter((payment) => payment.amount > 0)

  const cashFlow: GTCashFlowInput = {
    type: note.operationType as GTCashFlowTypeInput,
    payments
  }

  return {
    id: tripId,
    category: note.category as GTTripNoteCategoryInput,
    message: note.message || '',
    cashFlow: note.paymentIncidents ? cashFlow : undefined
  }
}

export const mapNoteFormToRSVPNoteInput = (
  tripId: string,
  driverId: string,
  note: INoteFormValues,
  filePaths: string[]
): AddTripDriverRsvpNoteInput => {
  return {
    id: tripId,
    driverID: driverId,
    status: note.driverRSVPStatus as TripDriverRsvpNoteStatus,
    message: note.message || '',
    filePaths
  }
}
export const mapNoteFormToRealDistanceInput = (
  tripId: string,
  distance: number,
  note: INoteFormValues,
  filePaths: string[]
): RegisterTripOdometerDistanceInput => {
  return {
    id: tripId,
    distance: kilometersToMeters(distance),
    message: note.message || '',
    filePaths
  }
}

interface IBusinessClientsToString {
  name: string
  businessSites?: { name: string }[]
}
export const businessClientsToString = (businessClients: IBusinessClientsToString[]): string => {
  if (isEmpty(businessClients)) {
    return '-'
  }

  return businessClients
    .map((businessClient): string => {
      let businessSitesStr = ''
      if (!isEmpty(businessClient.businessSites)) {
        businessSitesStr = ` (${businessClient.businessSites?.map(({ name }): string => name).join(', ')})`
      }
      return `${businessClient.name}${businessSitesStr}`
    })
    .join(', ')
}

export const getTripDateForOverride = (trip: GTTripV2): string => {
  const departureAtWithTimezone = dateToString(trip.departureAt, trip.itinerary[0].place.timezone)
  const [year, month, day] = departureAtWithTimezone.split('/')
  return format(new Date(parseInt(day), parseInt(month) - 1, parseInt(year)), US_DATE_FORMAT)
}

export const mapNewTripDriverToVehicleSchedule = (
  trip: GTTripV2,
  driver: IChangeDriverForm['driver']
): GTCreateRouteServiceVehicleScheduleInput => {
  const departureAtWithTimezone = dateToString(trip.departureAt, trip.itinerary[0].place.timezone)
  const [year, month, day] = departureAtWithTimezone.split('/')
  const departureAt = format(new Date(parseInt(day), parseInt(month) - 1, parseInt(year)), US_DATE_FORMAT)
  const vehicleIsConfiguredWithFrequency = trip.vehicle?.schedule?.type === GTTripV2VehicleScheduleType.Frequency

  return {
    startAt: departureAt,
    endAt: departureAt,
    vehicleID: trip.vehicle?.id || '',
    driverID: driver?.id,
    dow: !vehicleIsConfiguredWithFrequency
      ? [getDayLabel(mapDayToOperationDay(parseDate(departureAt).getDay())).toUpperCase()]
      : undefined,
    frequency: vehicleIsConfiguredWithFrequency
      ? {
          value: 1,
          unit: GTRouteServiceScheduleFrequencyUnit.Day
        }
      : undefined
  }
}

export const getItineraryPlaceByPlaceId = (
  itinerary: TripV2ItineraryStop[],
  placeId: string
): TripV2ItineraryStop | undefined => {
  return itinerary.find((stop) => stop.place.id === placeId)
}

export const CANCELLATION_CATEGORY_MAP = {
  [GTCancellationCategory.Vehicle]: [
    GTCancellationSubcategory.Accident,
    GTCancellationSubcategory.ScheduledMaintenance,
    GTCancellationSubcategory.MechanicalFailure
  ],
  [GTCancellationCategory.ExternalConditions]: [
    GTCancellationSubcategory.UnitStoppage,
    GTCancellationSubcategory.Weather,
    GTCancellationSubcategory.Traffic,
    GTCancellationSubcategory.Roadblock,
    GTCancellationSubcategory.Protest,
    GTCancellationSubcategory.Security
  ],
  [GTCancellationCategory.Planning]: [],
  [GTCancellationCategory.Partner]: [],
  [GTCancellationCategory.Driver]: [
    GTCancellationSubcategory.Disability,
    GTCancellationSubcategory.Absence,
    GTCancellationSubcategory.Other
  ],
  [GTCancellationCategory.BusinessClient]: [],
  [GTCancellationCategory.Other]: []
}

export interface GetVehicleForTripDetailsResponse {
  id: string
  name: string
  externalID: string
  template: {
    capacity: number
  }
  licensePlates: {
    number: string
  }[]
}

export const filterModifiableConfirmationModalitySalesChannels = (
  salesChannels: GTTripV2ConfirmationModalitySalesChannel[]
): GTTripV2ConfirmationModalitySalesChannel[] => {
  return salesChannels.filter((salesChannel) => {
    return salesChannel.id !== process.env.POS_MX_SALES_CHANNEL_ID
  })
}

export const filterNotModifiableConfirmationModalitySalesChannels = (
  salesChannels: GTTripV2ConfirmationModalitySalesChannel[]
): GTTripV2ConfirmationModalitySalesChannel[] => {
  return salesChannels.filter((salesChannel) => {
    return salesChannel.id === process.env.POS_MX_SALES_CHANNEL_ID
  })
}

const isTripVerticalEligibleForModifyingModality = (vertical: GTTripV2Vertical): boolean => {
  return vertical === GTTripV2Vertical.Travel
}

export const canRemoveReservedModality = (trip: Pick<GTTripV2, 'vertical' | 'confirmationModalities'>): boolean => {
  if (!isTripVerticalEligibleForModifyingModality(trip.vertical)) {
    return false
  }
  const reservedModality = trip.confirmationModalities.find(
    (modality) => modality.name === GTTripV2ConfirmationModalityName.Reserved
  )
  return !isEmpty(filterModifiableConfirmationModalitySalesChannels(reservedModality?.salesChannels || []))
}

export const canAddReservedModality = (trip: Pick<GTTripV2, 'vertical' | 'confirmationModalities'>): boolean => {
  if (!isTripVerticalEligibleForModifyingModality(trip.vertical)) {
    return false
  }
  return !canRemoveReservedModality(trip)
}

interface IGetPunctualityStatus {
  deadline: Date
  checkIn: Date
  thresholds: Record<string, number>
}

export const getPunctualityStatus = ({ deadline, checkIn, thresholds }: IGetPunctualityStatus): EPunctualityStatus => {
  const delay = differenceInMinutes(checkIn, deadline)

  const values = Object.entries(thresholds)
  for (const [status, threshold] of values) {
    if (delay <= threshold) {
      return status as EPunctualityStatus
    }
  }
  return EPunctualityStatus.SEVERE
}

export const getTripStopStatuses = (trip: TripV2): ITripStopStatus[] => {
  const departureAtWithTimezone = dateToString(trip.departureAt, trip.itinerary[0].place.timezone)
  const [year, month, day] = departureAtWithTimezone.split('/')
  const departureAt = new Date(parseInt(day), parseInt(month) - 1, parseInt(year))

  const stopsCheckedIn = trip.itinerary.filter((stop) => !isEmpty(stop.checkIns))

  return stopsCheckedIn.map((stop, index) => {
    const { arrival, departure } = stop
    const checkInDeadline = arrival || departure || { time: '00:00', day: 0 }
    const checkInDeadlineInMinutes = convert24HourToMinutes(checkInDeadline.time) + checkInDeadline.day * 24 * 60
    const checkInDeadlineDate = setMinutes(departureAt, checkInDeadlineInMinutes)

    const checkIn =
      findLast(stop.checkIns, (checkIn) => checkIn.method === TripV2ItineraryStopCheckMethod.Automatic) ||
      stop.checkIns?.at(-1)
    const checkInWithTimezone = dateToTimeString(checkIn?.at, stop.place.timezone)
    const [date, time] = checkInWithTimezone.split(', ')
    const [checkInDay, checkInMonth, checkInYear] = date.split('/')
    const [checkInHour, checkInMinutes] = time.split(':')
    const checkInDate = new Date(
      parseInt(checkInYear),
      parseInt(checkInMonth) - 1,
      parseInt(checkInDay),
      parseInt(checkInHour),
      parseInt(checkInMinutes)
    )

    return {
      status: getPunctualityStatus({
        deadline: checkInDeadlineDate,
        checkIn: checkInDate,
        thresholds: punctualityThresholds
      }),
      arrivalTime: convertHour24ToHour12Time(arrival?.time),
      departureTime: convertHour24ToHour12Time(departure?.time),
      place: stop.place,
      isOrigin: index === 0,
      isDestination: index === stopsCheckedIn.length - 1,
      checkIns:
        stop.checkIns?.reduce((acc, checkIn) => {
          // only keep last checkIn per method
          const checkInMethod = checkIn.method
          const existingCheckIn = acc.find((checkIn) => checkIn.method === checkInMethod)
          if (existingCheckIn) {
            // remove previsous checkIn
            acc = acc.filter((checkIn) => checkIn.method !== checkInMethod)
          }
          return [...acc, { ...checkIn }]
        }, [] as TripV2ItineraryStopCheck[]) || []
    }
  })
}
